/**
 * Centralized API error handler.
 *
 * Parses errors thrown by `apiRequest` and dispatches system toasts via the
 * notification store. Also handles 401 (session expired) by navigating to
 * the login page.
 *
 * Usage — the middleware is called automatically by `apiRequest` on failure.
 * Callers that handle errors locally (e.g. auth pages showing inline errors)
 * can pass `{ silent: true }` in the request options to suppress toasts.
 */

import type { ApiError } from "./client";
import { useNotificationStore } from "@/stores/notification-store";
import { showPlanLimitToast } from "@/lib/upgrade-toast";
import { useCompanionStore } from "@/stores/companion-store";
import { captureException } from "@/lib/sentry";
import { translate } from "@/i18n";
import { libErrorsMessages } from "@/i18n/messages/lib-errors";

/** Monotonic counter to guarantee unique toast IDs even for rapid-fire errors. */
let errorSeq = 0;

/** Options that callers can pass to suppress automatic error toasts. */
export interface ErrorMiddlewareOptions {
  /** When true, skip the global toast — the caller handles the error UI. */
  silent?: boolean;
  /** Optional context label for the toast (e.g. "Loading spaces"). */
  context?: string;
}

/** Type guard for ApiError (thrown by apiRequest on non-2xx responses). */
function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === "object" && err !== null && "status" in err && "data" in err
  );
}

/**
 * Type guard for network / fetch errors (no response received).
 * All browsers throw TypeError on network failure but the message varies:
 * Chrome: "Failed to fetch", Firefox: "NetworkError...", Safari: "Load failed".
 */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

function getNetworkErrorUrl(err: unknown): string | null {
  if (
    typeof err === "object" &&
    err !== null &&
    "requestUrl" in err &&
    typeof err.requestUrl === "string"
  ) {
    return err.requestUrl;
  }
  return null;
}

function summarizeRequestUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

/**
 * Extract a human-readable message from an API error response.
 *
 * Handles the common Django/DRF response shapes:
 *   - { error: "..." }
 *   - { detail: "..." }
 *   - { non_field_errors: ["..."] }
 */
function extractMessage(data: Record<string, unknown>): string | null {
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.non_field_errors)) {
    return (data.non_field_errors as string[]).join(" ");
  }
  return null;
}

/**
 * Handle an error thrown by `apiRequest`.
 *
 * Called automatically from the API client. Returns immediately if the error
 * is a 401 (session expired) since the AuthProvider + AuthGuard already
 * handle the redirect to login.
 */
export function handleApiError(
  error: unknown,
  options: ErrorMiddlewareOptions = {},
): void {
  if (options.silent) return;

  const { addSystemToast } = useNotificationStore.getState();
  const prefix = options.context ? `${options.context}: ` : "";

  // --- 401: session expired / not authenticated ---
  // AuthProvider.refreshUser() will clear the user, AuthGuard redirects.
  // No toast needed — the redirect is the feedback.
  if (isApiError(error) && error.status === 401) {
    return;
  }

  // --- 403: plan limit exceeded → upgrade toast ---
  if (
    isApiError(error) &&
    error.status === 403 &&
    error.data.code === "plan_limit_exceeded"
  ) {
    const msg =
      extractMessage(error.data) ??
      translate(libErrorsMessages, "planLimitReached");
    showPlanLimitToast(msg);
    return;
  }

  // --- 403: forbidden ---
  if (isApiError(error) && error.status === 403) {
    const msg =
      extractMessage(error.data) ??
      translate(libErrorsMessages, "permissionDenied");
    addSystemToast({
      id: `api-error-${++errorSeq}`,
      title: `${prefix}${msg}`,
      level: "error",
    });
    return;
  }

  // One shared notice for a throttled read bucket, including notices deferred
  // during onboarding. Unique IDs would replay every failed background read.
  if (isApiError(error) && error.status === 429) {
    addSystemToast({
      id: "api-rate-limit",
      title: translate(libErrorsMessages, "tooManyRequests"),
      description:
        extractMessage(error.data) ??
        translate(libErrorsMessages, "waitAndRetry"),
      level: "warning",
    });
    return;
  }

  // --- 4xx client errors (400, 404, 409, 422, etc.) ---
  if (isApiError(error) && error.status >= 400 && error.status < 500) {
    const msg =
      extractMessage(error.data) ??
      translate(libErrorsMessages, "requestFailed");
    addSystemToast({
      id: `api-error-${++errorSeq}`,
      title: `${prefix}${msg}`,
      level: "error",
    });
    return;
  }

  // --- 5xx server errors ---
  if (isApiError(error) && error.status >= 500) {
    captureException(error, { status: error.status, context: options.context });
    addSystemToast({
      id: `api-error-${++errorSeq}`,
      title: `${prefix}${translate(libErrorsMessages, "serverWentWrong")}`,
      description: translate(libErrorsMessages, "retryInMoment"),
      level: "error",
    });
    return;
  }

  // --- Network error (no response) ---
  // One stable toast: rapid-fire failures during an outage update it instead
  // of stacking (addSystemToast upserts by id). Skipped while the outage is
  // already known: the socket is reconnecting or failed (its own
  // "Connection failed" toast) or the companion disconnect toast is due, so a
  // single outage never reads as three.
  if (isNetworkError(error)) {
    const companion = useCompanionStore.getState();
    const outageSurfaced =
      companion.wsConnectionState === "reconnecting" ||
      companion.wsConnectionState === "failed" ||
      (companion.hasToken === true && companion.status === "disconnected");
    if (outageSurfaced) return;
    const requestUrl = getNetworkErrorUrl(error);
    addSystemToast({
      id: "api-network-error",
      title: translate(libErrorsMessages, "networkError"),
      description: requestUrl
        ? translate(libErrorsMessages, "couldNotReach", {
            host: summarizeRequestUrl(requestUrl),
          })
        : translate(libErrorsMessages, "checkConnection"),
      level: "error",
    });
    return;
  }

  // --- Unknown error shape ---
  captureException(error, { context: options.context, shape: "unknown" });
  if (import.meta.env.DEV) {
    console.error("[api-error-middleware]", error);
  }
  addSystemToast({
    id: `api-error-${++errorSeq}`,
    title: `${prefix}${translate(libErrorsMessages, "unexpectedError")}`,
    level: "error",
  });
}
